import React from 'react';
import { BrioModalField, BrioInput } from '../../components/BrioComponents';
import {
  DESCRIPTION_PARTS, MIN_DESCRIPTION_PARTS, composeDescription, countFilledParts,
} from '../utils/descriptionParts';

// ============================================================================
// VersionFields — the shared spec form for a library item version
// ============================================================================
// One place for the fields that describe an item version (short description,
// unit, measurement ref, the three long specs, and an optional change reason).
// The short description is typed as five parts, Location; Type; Material;
// Thickness; Size, at least two of them filled, and previewed as the one line
// it will read as everywhere else (see utils/descriptionParts.js). Editing a
// version saved before the parts existed shows its old free text above the
// boxes, so it can be split into them.
// Reused by the create-item, create-version and (soon) the item-editor flows
// so the wording, hints and ordering stay consistent.
//
// Controlled: pass `value` (the form object) and `onChange(field, value)`.
// ============================================================================

export default function VersionFields({
  value,
  onChange,
  includeChangeReason = false,
  changeReasonRequired = false,
  autoFocusDescription = false,
}) {
  const set = (field) => (e) => onChange(field, e.target.value);
  const composed = composeDescription(value);
  const filled = countFilledParts(value);
  // A version from before the parts: its free text, set by the form when it
  // opened (descriptionFormFields), and shown for the whole edit so it can be
  // read while it is split into the boxes.
  const legacyText = value.legacyDescription || null;

  return (
    <>
      <BrioModalField
        label={(
          <>
            Short description * (fill at least {MIN_DESCRIPTION_PARTS} of 5)
            {/* A version from before the parts: its old free text, on the
                label's line, in its own smaller muted style. */}
            {legacyText && (
              <span style={{ marginLeft: '1rem', fontSize: '0.78rem', fontWeight: 400, color: 'var(--brio-text-muted)' }}>
                Current description: <strong style={{ color: 'var(--brio-text, inherit)' }}>{legacyText}</strong>
              </span>
            )}
          </>
        )}
        hint={composed
          ? <>Reads as: <strong>{composed}</strong></>
          : 'Filled parts are joined in this order, separated by semicolons.'}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem' }}>
          {DESCRIPTION_PARTS.map((p, i) => (
            <label key={p.key} style={{ display: 'block', minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--brio-text-muted)', marginBottom: 2 }}>
                {p.label}
              </span>
              <BrioInput
                value={value[p.key] || ''}
                onChange={set(p.key)}
                placeholder={p.placeholder}
                autoFocus={autoFocusDescription && i === 0}
              />
            </label>
          ))}
        </div>
        {filled > 0 && filled < MIN_DESCRIPTION_PARTS && (
          <div style={{ fontSize: '0.74rem', color: '#a94442', marginTop: '0.3rem' }}>
            Fill at least one more part.
          </div>
        )}
      </BrioModalField>
      <BrioModalField label="Unit *" hint="e.g. m³, m², kg, no., lump sum">
        <BrioInput value={value.unit || ''} onChange={set('unit')} placeholder="m³" />
      </BrioModalField>
      <BrioModalField label="Measurement reference" hint="Optional — e.g. IS 1200 Part 3.">
        <BrioInput value={value.measurementRef || ''} onChange={set('measurementRef')} placeholder="IS 1200 Part 3" />
      </BrioModalField>
      <BrioModalField label="Long spec — General">
        <textarea value={value.longSpecGeneral || ''} onChange={set('longSpecGeneral')} className="brio-modal-input" rows={2} placeholder="Scope, intent and standards" />
      </BrioModalField>
      <BrioModalField label="Long spec — Products">
        <textarea value={value.longSpecProducts || ''} onChange={set('longSpecProducts')} className="brio-modal-input" rows={2} placeholder="Materials, brands, grades" />
      </BrioModalField>
      <BrioModalField label="Long spec — Execution">
        <textarea value={value.longSpecExecution || ''} onChange={set('longSpecExecution')} className="brio-modal-input" rows={2} placeholder="Workmanship, tolerances, testing" />
      </BrioModalField>
      {includeChangeReason && (
        <BrioModalField
          label={`Change reason${changeReasonRequired ? ' *' : ''}`}
          hint="Why does this version exist? e.g. tighter tolerances, vendor change, scope update."
        >
          <textarea value={value.changeReason || ''} onChange={set('changeReason')} className="brio-modal-input" rows={2} placeholder="Tightened slip rating to R10 per spec revision 2026-05-15" />
        </BrioModalField>
      )}
    </>
  );
}
